import type { AmhsTransferFrame } from '../../types/amhsStream';

/**
 * A processed FOUP resting on a tool dock, waiting for its outbound pickup.
 *
 * Derived from streamed `transfer_event` frames: an equipment DROP starts the
 * process window and the matching PICKUP closes it.
 */
export type ToolPickupReadyEntry = {
  toolId: string;
  portId: string;
  foupId: string;
  /** Simulation time the FOUP was dropped onto the tool. */
  droppedSimTimeS: number;
  /** Simulation time the tool finishes processing and the FOUP may be taken. */
  readySimTimeS?: number;
  /** Simulation time the FOUP leaves, when the stream already announced it. */
  pickedUpSimTimeS?: number;
};

export type ToolFoupOccupancy = ToolPickupReadyEntry & {
  status: 'processing' | 'ready';
};

function trimmed(value: string | undefined): string {
  return value?.trim() ?? '';
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * When processing finishes. `materialReadySimTimeS` is authoritative; the dwell
 * and the OHT resume time are fallbacks for streams that omit it.
 */
function resolveReadySimTimeS(frame: AmhsTransferFrame): number | undefined {
  if (finite(frame.materialReadySimTimeS)) return frame.materialReadySimTimeS;
  if (finite(frame.processWaitSec) && frame.processWaitSec >= 0) {
    return frame.simTimeS + frame.processWaitSec;
  }
  if (finite(frame.resumeSimTimeS)) return frame.resumeSimTimeS;
  return undefined;
}

/**
 * Fold streamed transfers into one entry per FOUP currently held by a tool.
 *
 * Frames without a `toolId` (stocker and gate handoffs) are ignored. A DROP
 * the stream cannot date is kept as occupancy but is never announced as ready.
 */
export function collectToolPickupReady(
  frames: Iterable<AmhsTransferFrame>
): ToolPickupReadyEntry[] {
  const byKey = new Map<string, ToolPickupReadyEntry>();
  const ordered = [...frames].sort(
    (a, b) => a.simTimeS - b.simTimeS || (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  for (const frame of ordered) {
    const toolId = trimmed(frame.toolId);
    const portId = trimmed(frame.portId);
    const foupId = trimmed(frame.foupId);
    if (!toolId || !portId || !foupId) continue;

    const key = `${portId}::${foupId}`;
    if (frame.eventType === 'DROP') {
      const readySimTimeS = resolveReadySimTimeS(frame);
      byKey.set(key, {
        toolId,
        portId,
        foupId,
        droppedSimTimeS: frame.simTimeS,
        ...(readySimTimeS != null ? { readySimTimeS } : {}),
      });
      continue;
    }

    // PICKUP frames are published ahead of the clock, so the entry records when
    // the FOUP leaves rather than disappearing as soon as the stream says so.
    const entry = byKey.get(key);
    if (entry) entry.pickedUpSimTimeS = frame.simTimeS;
  }

  return [...byKey.values()];
}

function isOnTool(entry: ToolPickupReadyEntry, simTimeS: number): boolean {
  return (
    simTimeS >= entry.droppedSimTimeS &&
    (entry.pickedUpSimTimeS == null || simTimeS < entry.pickedUpSimTimeS)
  );
}

function isPickupReady(entry: ToolPickupReadyEntry, simTimeS: number): boolean {
  return entry.readySimTimeS != null && simTimeS >= entry.readySimTimeS && isOnTool(entry, simTimeS);
}

/** Entries whose FOUP has finished processing and is still on the tool. */
export function pickupReadyAtTime(
  entries: readonly ToolPickupReadyEntry[],
  simTimeS: number
): ToolPickupReadyEntry[] {
  return entries.filter((entry) => isPickupReady(entry, simTimeS));
}

/** FOUPs currently inside a tool — still processing or waiting for pickup. */
export function toolFoupsAtTime(
  entries: readonly ToolPickupReadyEntry[],
  simTimeS: number
): ToolFoupOccupancy[] {
  return entries.filter((entry) => isOnTool(entry, simTimeS)).map((entry) => ({
    ...entry,
    status: isPickupReady(entry, simTimeS) ? 'ready' : 'processing',
  }));
}

/** Occupied FOUPs grouped by tool, in stream order. */
export function toolFoupsByTool(
  entries: readonly ToolPickupReadyEntry[],
  simTimeS: number
): Record<string, ToolFoupOccupancy[]> {
  const byTool: Record<string, ToolFoupOccupancy[]> = {};
  for (const entry of toolFoupsAtTime(entries, simTimeS)) {
    (byTool[entry.toolId] ??= []).push(entry);
  }
  return byTool;
}

/** Ready FOUP ids per tool, in stream order. */
export function pickupReadyFoupsByTool(
  entries: readonly ToolPickupReadyEntry[],
  simTimeS: number
): Record<string, string[]> {
  const byTool: Record<string, string[]> = {};
  for (const entry of pickupReadyAtTime(entries, simTimeS)) {
    (byTool[entry.toolId] ??= []).push(entry.foupId);
  }
  return byTool;
}
